import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Mic, Sparkles, ScanText, Volume2, ArrowRight } from "lucide-react";

import { API } from "../lib/api";
import ScoreRing from "../components/ScoreRing";
import AudioPlayer from "../components/AudioPlayer";

const subscoreColor = (n) => (n >= 80 ? "bg-sage" : n >= 60 ? "bg-terracotta" : "bg-amber-500");

export default function PublicSession() {
    const { sessionId } = useParams();
    const [session, setSession] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`${API}/public/sessions/${sessionId}`)
            .then(({ data }) => setSession(data))
            .catch((e) => setError(e?.response?.status === 404 ? "This scorecard is private or doesn't exist." : "Couldn't load this scorecard."));
    }, [sessionId]);

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center vf-grain p-6 text-center" data-testid="public-session-error">
                <div>
                    <div className="font-serif text-4xl italic text-stone-500">{error}</div>
                    <Link to="/" className="inline-flex mt-6 items-center gap-2 text-sm text-terracotta">
                        Open Cadence <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        );
    }

    if (!session) {
        return <div className="min-h-screen flex items-center justify-center text-stone-500 italic font-serif text-2xl vf-grain" data-testid="public-session-loading">Loading scorecard…</div>;
    }

    const a = session.analysis || {};
    const m = session.metrics || {};
    const sc = a.scores || {};

    return (
        <div className="min-h-screen vf-grain pb-12" data-testid="public-session-page">
            {/* Top bar */}
            <div className="px-5 sm:px-8 py-5 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-terracotta flex items-center justify-center text-white">
                        <Mic size={14} strokeWidth={1.5} />
                    </div>
                    <span className="font-serif text-2xl tracking-tight">Cadence</span>
                </Link>
                <Link
                    to="/"
                    className="text-xs tracking-[0.2em] uppercase font-bold text-terracotta hover:opacity-80"
                    data-testid="public-cta"
                >
                    Try a rep yourself →
                </Link>
            </div>

            <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-2">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400">
                        {session.shared_by} · {session.prompt_category}
                    </div>
                    <h1 className="font-serif text-3xl sm:text-5xl tracking-tight font-light mt-2">{session.prompt_title}</h1>
                    <p className="mt-3 font-serif italic text-stone-700 dark:text-stone-300 text-base sm:text-lg leading-relaxed">
                        “{session.prompt_text}”
                    </p>
                </motion.div>

                <div className="mt-8 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 backdrop-blur-md p-7 sm:p-8 grid sm:grid-cols-[auto_1fr] gap-8 items-center" data-testid="public-summary">
                    <ScoreRing value={a.overall_score || 0} />
                    <div>
                        <p className="font-serif text-2xl sm:text-3xl font-light leading-snug italic">
                            “{a.one_line_takeaway || ""}”
                        </p>
                        <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                            <div>
                                <div className="font-serif text-3xl font-light">{m.wpm || "—"}</div>
                                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mt-1">WPM</div>
                            </div>
                            <div>
                                <div className="font-serif text-3xl font-light">{m.filler_count ?? "—"}</div>
                                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mt-1">Fillers</div>
                            </div>
                            <div>
                                <div className="font-serif text-3xl font-light">
                                    {Math.floor((m.duration_sec || 0) / 60)}:{((m.duration_sec || 0) % 60).toString().padStart(2, "0")}
                                </div>
                                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mt-1">Length</div>
                            </div>
                        </div>
                    </div>
                </div>

                {session.audio_path && (
                    <div className="mt-6 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-5">
                        <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400 mb-3">
                            <Volume2 size={12} strokeWidth={1.5} /> Listen
                        </div>
                        <AudioPlayer sessionId={session.id} publicMode />
                    </div>
                )}

                <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="public-subscores">
                    {[
                        { key: "clarity", label: "Clarity" },
                        { key: "structure", label: "Structure" },
                        { key: "content_relevance", label: "Content" },
                        { key: "delivery", label: "Delivery" },
                    ].map((s) => {
                        const v = sc[s.key] || 0;
                        return (
                            <div key={s.key} className="rounded-2xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-5">
                                <div className="flex items-baseline justify-between">
                                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">{s.label}</div>
                                    <div className="font-serif text-3xl font-light">{v}</div>
                                </div>
                                <div className="mt-3 h-1.5 bg-stone-100 dark:bg-stone-800 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${subscoreColor(v)}`} style={{ width: `${v}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    <div className="rounded-3xl border border-sage/20 bg-sage/5 p-6">
                        <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-sage">
                            <Sparkles size={12} strokeWidth={1.5} /> What worked
                        </div>
                        <ul className="mt-3 space-y-2 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                            {(a.strengths || []).map((s, i) => (
                                <li key={i} className="flex gap-2"><span className="text-sage mt-1.5">•</span>{s}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="rounded-3xl border border-terracotta/30 bg-terracotta/5 p-6">
                        <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-terracotta">
                            <ScanText size={12} strokeWidth={1.5} /> What to sharpen
                        </div>
                        <ul className="mt-3 space-y-2 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                            {(a.weaknesses || []).map((s, i) => (
                                <li key={i} className="flex gap-2"><span className="text-terracotta mt-1.5">•</span>{s}</li>
                            ))}
                        </ul>
                    </div>
                </div>

                {a.ideal_answer && (
                    <div className="mt-6 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 backdrop-blur-md p-6 sm:p-7">
                        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">An ideal answer</div>
                        <p className="font-serif text-lg sm:text-xl leading-relaxed mt-3 italic text-stone-800 dark:text-stone-200 whitespace-pre-wrap">
                            {a.ideal_answer}
                        </p>
                    </div>
                )}

                {/* Marketing CTA */}
                <div className="mt-10 text-center rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-gradient-to-br from-terracotta/5 to-sage/5 p-8" data-testid="public-marketing-cta">
                    <div className="font-serif text-3xl sm:text-4xl font-light tracking-tight">Want a scorecard like this?</div>
                    <p className="text-sm text-stone-600 mt-2">Practice any prompt. Cadence transcribes, scores, and shows you the one line to fix next time.</p>
                    <Link
                        to="/"
                        className="inline-flex mt-5 items-center gap-2 rounded-full px-7 h-12 bg-terracotta hover:bg-[#D05A40] text-white font-medium"
                    >
                        Try Cadence free <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        </div>
    );
}
